import React, { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../../api";
import type { ExecutiveAlert } from "../../types";
import { useToast } from "../../context/ToastContext";
import { formatDateTime } from "../../lib/format";

function severityClass(sev?: string) {
  if (sev === "HIGH") return "bad";
  if (sev === "MEDIUM") return "warn";
  return "ok";
}

// §11 + tests #21-25: alerts must be auditable — current/baseline/threshold
// values are shown as-is (never recomputed client-side) and acknowledging
// records the *calling user*, taken server-side from the JWT, never a
// client-supplied id.
export default function AlertsPanel({ canAcknowledge }: { canAcknowledge: boolean }) {
  const { show } = useToast();
  const [alerts, setAlerts] = useState<ExecutiveAlert[]>([]);
  const [statusFilter, setStatusFilter] = useState<"OPEN" | "ALL">("OPEN");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getAlerts(statusFilter === "OPEN" ? { status: "OPEN" } : undefined)
      .then((rows) => setAlerts(Array.isArray(rows) ? rows : []))
      .catch((e) => setError(e instanceof ApiClientError ? e.message : "No se pudieron cargar las alertas."))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function acknowledge(id: any) {
    setBusyId(String(id));
    try {
      await api.acknowledgeAlert(id);
      show("Alerta reconocida.", "success");
      load();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo reconocer la alerta.", "error");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <div className="pipeline-tabs">
          <button className={`tab ${statusFilter === "OPEN" ? "active" : ""}`} onClick={() => setStatusFilter("OPEN")}>
            Abiertas
          </button>
          <button className={`tab ${statusFilter === "ALL" ? "active" : ""}`} onClick={() => setStatusFilter("ALL")}>
            Todas
          </button>
        </div>
      </div>

      {loading && <div className="loading-strip">Cargando alertas...</div>}
      {error && <p className="form-error">{error}</p>}

      <table>
        <thead>
          <tr>
            <th>Severidad</th>
            <th>Alerta</th>
            <th>Área</th>
            <th>Actual</th>
            <th>Baseline</th>
            <th>Umbral</th>
            <th>Estado</th>
            {canAcknowledge && <th>Acción</th>}
          </tr>
        </thead>
        <tbody>
          {alerts.map((a) => (
            <tr key={a.id}>
              <td className={severityClass(a.severity)}>{a.severity || "-"}</td>
              <td>{a.alert_type}</td>
              <td>{a.dimension_value || a.dimension_type || "-"}</td>
              <td>{a.current_value ?? "-"}</td>
              <td>{a.baseline_value ?? "-"}</td>
              <td>{a.threshold_value ?? "-"}</td>
              <td>
                <span className="pill">{a.status || "OPEN"}</span>
                {a.acknowledged_at && <div className="meta">{formatDateTime(a.acknowledged_at)}</div>}
              </td>
              {canAcknowledge && (
                <td>
                  {a.status !== "ACKNOWLEDGED" ? (
                    <button className="link-btn" disabled={busyId === String(a.id)} onClick={() => acknowledge(a.id)}>
                      Reconocer
                    </button>
                  ) : (
                    <span className="meta">Reconocida</span>
                  )}
                </td>
              )}
            </tr>
          ))}
          {!loading && alerts.length === 0 && (
            <tr>
              <td colSpan={canAcknowledge ? 8 : 7} className="meta">
                Sin alertas para este filtro.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
